/*
 * FIN — Reconciliation Workbench figure.
 * Geometry + state animation ported 1:1 from FIN_Reconciliation_Workbench v1 / reconcile.js.
 * Uses the single shared Hairline kernel (src/components/feature/hairline/kernel.ts).
 * Hairline — https://github.com/lucasmarkes/hairline (commit bc782244), MIT License,
 * Copyright (c) Lucas Markes. Full license text is kept in the kernel file header.
 * Only change from the source: no global `hairline()` registration (mount is exported directly).
 */
/* eslint-disable */
import {
  Cam, fit, proj, facing, rings, prism, solid, put, mk, seg, lerp,
  tween, tset, tval, tdone, register, pointer, disposer,
} from '@/components/feature/hairline/kernel';

export type FinState = 'rest' | 'matching' | 'matched' | 'review';

export interface FinHandle {
  set: (v: number) => void;
  setState: (next: FinState) => void;
  setAutomatic: (on: boolean) => void;
  onState: (fn: (s: FinState) => void) => void;
  destroy: () => void;
}

interface Ctx { stage: Element; svg: SVGSVGElement; read: { textContent: string } }

export function mountReconcile({ stage, svg, read }: Ctx, value: number): FinHandle {
  const bag = disposer(), C = Cam(45, .5, 1.65);
  let strength = value, state = 'rest', last = '', automatic = false, elapsed = 0, phase = -1, reported = 'rest', onState: (s: any) => void = () => {};
  const world = (u, v, z) => [(u + v) / Math.sqrt(2), (v - u) / Math.sqrt(2), z];
  fit(C, [world(-110, -72, 0), world(112, 85, 0), world(-110, -72, 30), world(112, 85, 30)], 200, 150);
  const P = proj(C), front = facing(C), g = mk('g', {}, svg), fixed = mk('g', {}, g), dynamic = mk('g', {}, g);
  const pair = tween(0), archive = tween(0), exception = tween(0);
  function box(parent, x, y, w, d, z, h, r = 2) { const el = solid(parent), [ring, inner] = rings(x, y, x + w, y + d, r, .6); put(el, prism(P, front, ring, inner, z, z + h)); return el; }
  function record(parent, u, v, z, w = 36, d = 26, chosen = false) {
    const [cx, cy] = world(u, v, 0), x = cx - w / 2, y = cy - d / 2, el = box(parent, x, y, w, d, z, .8, 1.8); el.sil.classList.toggle('hi', chosen);
    const lines = mk('path', { class: 'nf lo' }, parent);
    lines.setAttribute('d', [7, 12, 17].map((dy, i) => seg(P(x + 5, y + dy, z + .8), P(x + w - 7 - i * 3, y + dy, z + .8))).join(''));
    return el;
  }
  function tray(parent, u, v, w, d) {
    const [cx, cy] = world(u, v, 0), x = cx - w / 2, y = cy - d / 2;
    box(parent, x, y, w, d, 0, 2.5, 3); box(parent, x, y, w, 2.5, 2.5, 3, 1);
    box(parent, x, y + 2.5, 2.5, d - 2.5, 2.5, 3, 1);
    return () => box(parent, x + w - 2.5, y + 2.5, 2.5, d - 2.5, 2.5, 3, 1);
  }
  const comparison = []; const items = [];
  items.push({ u: 0, v: 0, type: 'compare' }, { u: -76, v: -42, type: 'source' }, { u: -76, v: 43, type: 'source' },
    { u: 79, v: -28, type: 'matched' }, { u: 79, v: 58, type: 'review' });
  items.sort((a, b) => a.v - b.v).forEach((item) => {
    const group = mk('g', {}, fixed), { u, v, type } = item;
    if (type === 'compare') {
      const [x, y] = world(0, 0, 0);
      comparison.push(box(group, x - 26, y - 26, 52, 52, 0, 3, 4));
      comparison.push(box(group, x - 26, y - 26, 3, 52, 3, 5, 1.2));
      comparison.push(box(group, x + 23, y - 26, 3, 52, 3, 5, 1.2));
    } else {
      const close = tray(group, u, v, type === 'source' ? 44 : 48, 36);
      if (type !== 'review') for (let i = 0; i < 3; i++) record(group, u, v, 4 + i * 1.8, 35, 25);
      close();
    }
  });
  function draw(a, b, e) {
    dynamic.replaceChildren(); comparison.forEach((part) => part.sil.classList.toggle('hi', state === 'rest' || state === 'matching' || b < .99));
    const records = [];
    for (let i = 0; i < 2; i++) {
      const fromV = i === 0 ? -42 : 43, targetV = i === 0 ? -5 : 5;
      let u = lerp(-76, 0, a), v = lerp(fromV, targetV, a), z = lerp(10, 16, a) + Math.sin(Math.PI * a) * 4 * strength;
      u = lerp(u, 79, b); v = lerp(v, -28 + i * 1.2, b); z = lerp(z, 10 + i * 2, b);
      records.push({ u, v, z, chosen: state === 'matched' && b > .99 && i === 1 });
    }
    records.push({ u: lerp(-71, 83, e), v: lerp(50, 60, e), z: lerp(11, 7, e) + Math.sin(Math.PI * e) * 5 * strength, chosen: state === 'review' });
    records.sort((a2, b2) => a2.v - b2.v || a2.z - b2.z).forEach((r) => record(dynamic, r.u, r.v, r.z, 36, 26, r.chosen));
  }
  let B: any = null;
  function choose(next) {
    if (!['rest', 'matching', 'matched', 'review'].includes(next) || next === state) return;
    state = next; const now = performance.now();
    const needsAlignment = tval(pair, now) < .98;
    tset(pair, next === 'rest' ? 0 : 1, now, 0); tset(archive, next === 'matched' || next === 'review' ? 1 : 0, now, (next === 'matched' || next === 'review') && needsAlignment ? 700 : 0);
    tset(exception, next === 'review' ? 1 : 0, now, 0); read.textContent = next; last = ''; B?.wake();
  }
  draw(0, 0, 0); read.textContent = 'rest';
  B = register(stage, (dt, now) => {
    if (automatic) {
      elapsed += Math.min(dt, .1) * 1000; const nextPhase = Math.floor(elapsed / 2600) % 4;
      if (nextPhase !== phase) { phase = nextPhase; choose(['rest', 'matching', 'matched', 'review'][phase]); }
    }
    const a = tval(pair, now), b = tval(archive, now), e = tval(exception, now), key = [a, b, e, state, strength].join('|');
    if (key !== last) { draw(a, b, e); last = key; }
    /* Completion is only reported once the archive motion has actually landed. */
    const actual = (state === 'matched' || state === 'review') && b < .99 ? 'matching' : state;
    if (actual !== reported) { reported = actual; read.textContent = actual; onState(actual); }
    return automatic || !tdone(pair, now) || !tdone(archive, now) || !tdone(exception, now);
  }); bag.add(B.unregister);
  if (!(stage as Element).hasAttribute?.('data-controlled')) bag.add(pointer(stage, { move: ([x]) => choose(x > 290 ? 'review' : x > 150 ? 'matched' : 'matching'), leave: () => choose('rest') }));
  bag.add(() => svg.replaceChildren());
  return {
    set: (v) => { strength = v; last = ''; B.wake(); },
    setState: (next) => { automatic = false; choose(next); },
    setAutomatic: (on) => { automatic = on; elapsed = 0; phase = -1; B.wake(); },
    onState: (fn) => { onState = fn; fn(reported as FinState); },
    destroy: bag.dispose,
  };
}